// SEO-optimized long-form content for each service page.
// Each entry: h1, seoTitle, seoDescription, keywords, hero image, sections, benefits, faq

import { GALLERY_IMAGES, HERO_IMAGE } from "@/lib/api";

export const SERVICE_PAGES = {
  "window-tint-edmonton": {
    slug: "window-tint-edmonton",
    serviceSlug: "window-tint",
    title: "Window Tint",
    availabilityNote: "Temporarily unavailable — launching soon at our Edmonton studio. Join the waitlist and we'll contact you first.",
    h1: "Premium Window Tint in Edmonton",
    seoTitle: "Window Tint Edmonton — Ceramic Film Installation",
    seoDescription:
      "Professional ceramic window tint installation in Edmonton. Blocks harmful UV, rejects heat, cuts glare, keeps optical clarity.",
    keywords:
      "window tint Edmonton, ceramic window tint Edmonton, car window tinting Edmonton, tint shop Edmonton",
    hero: HERO_IMAGE,
    tag: "Heat & UV Rejection",
    related: ["ceramic-coating-edmonton", "ppf-edmonton", "interior-detailing-edmonton"],
    intro:
      "Professional-grade ceramic and carbon window tint — installed under controlled, dust-free conditions with computer-cut templates for a factory-perfect finish.",
    sections: [
      {
        heading: "The film we trust",
        body:
          "At WTD we will install only premium ceramic and nano-carbon window films. These are not the cheap dyed films you'll find in bargain-basement shops. Quality tint blocks harmful UV radiation, rejects a large share of total solar energy, and noticeably reduces cabin heat — welcome during Edmonton's summer sun and for year-round interior comfort. Ceramic film contains no metal, so it does not interfere with your vehicle's GPS, cellular, radio or keyless entry signals.",
      },
      {
        heading: "Installed in Alberta, built for Alberta",
        body:
          "Edmonton drivers see everything from -40°C winters to 30°C summers. Cheap films crack, bubble and turn purple after one hot summer. Our premium films are engineered to resist bubbling, peeling, cracking and fading — so the tint you install today keeps its clean look for years. Alberta law limits front-side window VLT to 70% or higher; rear side and rear windshield have no restrictions. We'll help you choose the right shade for both compliance and style.",
      },
      {
        heading: "Why WTD's install is different",
        body:
          "Every install starts with a full deep-clean of the glass and a controlled installation bay. We use computer-cut templates matched exactly to your vehicle's make and model — no razor blades near your factory seals. Edges are heat-sealed and inspected under focused lighting. The result: clean lines, no light gaps, no dust particles, no lifting.",
      },
    ],
    benefits: [
      "Strong UV rejection — helps protect skin and interior from fading",
      "Heat & glare reduction — exact specs depend on the selected film line",
      "Non-metallic ceramic — no signal interference",
      "Premium films engineered to resist bubbling, peeling & fading",
      "Alberta-VLT compliant options",
      "Computer-cut templates for precision fitment",
    ],
    faq: [
      {
        q: "What tint options do you offer?",
        a: "We work with premium ceramic and nano-carbon automotive window films in several shades (VLT levels). Ceramic film gives the strongest heat and UV rejection without any signal interference. We'll show you the shade options in person and help you pick one that fits both Alberta law and the look you want.",
      },
      {
        q: "How dark can I legally tint in Alberta?",
        a: "Front side windows must let more than 70% of light through (70% VLT). Rear side windows and the rear windshield have no VLT limit under Alberta law. We'll advise on the darkest legal setup for your vehicle.",
      },
      {
        q: "Will ceramic tint interfere with my phone or GPS?",
        a: "No. Ceramic and carbon films contain no metallic particles, so they will not block or degrade GPS, cellular, radio or key-fob signals.",
      },
      {
        q: "How long does the installation take?",
        a: "A full car tint typically takes 2 to 4 hours depending on the vehicle. SUVs and full-size trucks may take longer.",
      },
      {
        q: "How long until I can roll the windows down?",
        a: "We recommend waiting 3 to 5 days after installation for the adhesive to fully cure before rolling windows down.",
      },
      {
        q: "Do you offer a warranty?",
        a: "Warranty coverage depends on the film line selected — we'll confirm the exact terms for your vehicle before installation.",
      },
    ],
  },

  "ppf-edmonton": {
    slug: "ppf-edmonton",
    serviceSlug: "ppf",
    title: "Premium Paint Protection Film",
    h1: "Paint Protection Film in Edmonton",
    seoTitle: "PPF Edmonton — Premium Paint Protection Film & Clear Bra Installation",
    related: ["ceramic-coating-edmonton", "paint-correction-edmonton", "windshield-protection-film-edmonton"],
    seoDescription:
      "Premium Paint Protection Film in Edmonton — self-healing Veltik Clear PPF PRO 60 (7.5 mil) against rock chips, road debris and minor scratches. Packages from $699, individual panels, online booking.",
    keywords:
      "PPF Edmonton, paint protection film Edmonton, clear bra Edmonton, rock chip protection, self-healing PPF",
    hero: GALLERY_IMAGES[0],
    tag: "Self-Healing · Rock Chip Protection",
    intro:
      "Premium Paint Protection Film installed with Veltik Clear PPF PRO 60 — a 7.5 mil crystal-clear gloss film with a heat-activated self-healing surface. Physical protection against rock chips, road debris and minor scratches that preserves your original paint.",
    sections: [
      {
        heading: "Self-healing technology, crystal-clear finish",
        body:
          "Paint Protection Film (PPF), also called a clear bra, is a virtually invisible polyurethane film that bonds to your factory paint. We install Veltik Clear PPF PRO 60 — a premium 7.5 mil clear gloss film whose top coat self-heals minor scratches with heat from the sun or warm water. It absorbs the impact from rock chips, gravel spray, sand and road debris — the exact damage Edmonton drivers battle every year on Anthony Henday and Highway 2 — while preserving the original appearance of your paint.",
      },
      {
        heading: "Packages or individual panels — your call",
        body:
          "Four coverage levels: Partial Front (bumper, 24\" hood, partial fenders, mirrors, headlights) from $699, Full Front (full hood, full fenders, bumper, mirrors, headlights) from $1,299, Full Front Plus (adds A-pillars, roof leading edge and door cups) from $1,499, and Full Vehicle coverage from $3,799 with the final price confirmed after a free in-person vehicle inspection. The exact price for your vehicle appears in the calculator after you pick the year, make and model. Every panel is also available individually — pick exactly the coverage you want, driver side, passenger side or both. Prices are in CAD before GST; three-row and non-standard body vehicles are quoted individually.",
      },
      {
        heading: "PPF and ceramic coating work together",
        body:
          "They do different jobs. PPF physically absorbs impacts; ceramic coating adds gloss and hydrophobicity but no impact protection. Many clients combine both — PPF on high-impact panels and ceramic on top. Ask us to quote both together.",
      },
    ],
    benefits: [
      "Self-healing 7.5 mil Veltik Clear PPF PRO 60",
      "Physical protection from rock chips, road debris & minor scratches",
      "Crystal-clear gloss finish — preserves original paint appearance",
      "Packages from $699 or individual panels from $25",
      "Online booking — pick a preferred date, we confirm the time",
    ],
    faq: [
      {
        q: "What film do you install?",
        a: "We install Veltik Clear PPF PRO 60 — a premium 7.5 mil clear gloss paint protection film with a heat-activated self-healing surface for minor scratches. It is optically clear and does not change the look of healthy paint.",
      },
      {
        q: "How much coverage do I need?",
        a: "For most Edmonton drivers, Full Front (bumper, full hood, full fenders, mirrors, headlights) covers the vast majority of realistic damage risk. Partial Front is the budget-friendly minimum, Full Front Plus adds A-pillars, roof edge and door cups, and Full Vehicle wraps every painted panel.",
      },
      {
        q: "Can I protect just one panel?",
        a: "Yes. Every panel is priced individually in our calculator — from a front bumper or full hood down to door cups, door edges and fog lights. For sided parts (fenders, mirrors, doors, pillars) you can choose driver side, passenger side or both.",
      },
      {
        q: "How does booking work for PPF?",
        a: "PPF installs are scheduled individually. You pick a preferred date online and we confirm the exact drop-off time and duration when we confirm your booking. Full Vehicle projects are quoted after an in-person assessment — no payment is taken for an unconfirmed amount.",
      },
      {
        q: "Can PPF be removed later?",
        a: "Yes. Premium films can be safely removed by a trained installer without damaging the factory paint underneath.",
      },
      {
        q: "Is PPF the same as a ceramic coating or window tint?",
        a: "No. PPF is a physical film that absorbs impacts. Ceramic coating is a liquid layer for gloss and hydrophobicity. Window tint darkens glass for heat and privacy — our windshield protection film is a clear exterior film for impact protection, not tint.",
      },
    ],
  },

  "windshield-protection-film-edmonton": {
    slug: "windshield-protection-film-edmonton",
    serviceSlug: "windshield-protection",
    title: "Windshield Protection Film",
    h1: "Windshield Protection Film in Edmonton",
    seoTitle: "Windshield Protection Film Edmonton — Veltik SEC-H Exterior Glass Film",
    related: ["ppf-edmonton", "ceramic-coating-edmonton", "paint-correction-edmonton"],
    seoDescription:
      "Windshield Protection Film in Edmonton — Veltik SEC-H 6.5 mil exterior glass film. Hydrophobic, high optical clarity, helps protect against minor road debris damage. From $399, booked online.",
    keywords:
      "windshield protection film Edmonton, windshield film Edmonton, rock chip windshield protection, exterior glass film",
    hero: GALLERY_IMAGES[1],
    tag: "Hydrophobic Windshield Protection",
    intro:
      "Veltik SEC-H WPF — a 6.5 mil exterior windshield protection film with high optical clarity and a hydrophobic surface. It helps protect your glass against minor road debris damage and makes rain bead off for better visibility.",
    sections: [
      {
        heading: "An exterior protection film — not window tint",
        body:
          "This is a clear protective film applied to the outside of the windshield — not conventional window tint. Veltik SEC-H WPF is 6.5 mil thick, optically clear and hydrophobic: water beads and clears quickly in rain, and the film takes the hit from sand and small road debris before your glass does.",
      },
      {
        heading: "Honest expectations",
        body:
          "No film makes glass unbreakable, and we won't promise it prevents every chip or crack. What it does is add a sacrificial, hydrophobic layer that helps protect against minor road debris damage and everyday wiper-zone wear — and it's replaceable when it has done its job.",
      },
      {
        heading: "Simple pricing, booked online",
        body:
          "Windshield Protection Film is $399 for sedans and coupes, $449 for small SUVs and crossovers, $499 for large SUVs and $549 for pickup trucks (CAD, before GST). Oversized or complex glass is quoted individually. Add it to any PPF package in the calculator or book it on its own — pick a preferred date and we confirm the exact time.",
      },
    ],
    benefits: [
      "Veltik SEC-H WPF — 6.5 mil exterior glass film",
      "Hydrophobic surface — better visibility in rain",
      "High optical clarity — no change to the view",
      "Helps protect against minor road debris damage",
      "From $399 — book online in minutes",
    ],
    faq: [
      {
        q: "Is windshield protection film the same as window tint?",
        a: "No. Window tint is an interior film that darkens glass for heat rejection and privacy. Windshield Protection Film is a clear 6.5 mil exterior film designed to take minor impacts and add a hydrophobic surface — it does not tint your glass.",
      },
      {
        q: "Will it stop my windshield from cracking?",
        a: "No film can promise that. Veltik SEC-H helps protect against minor road debris damage and takes everyday abrasion, but a large enough impact can still chip or crack glass. The film is a sacrificial layer — and it can be replaced.",
      },
      {
        q: "How much does it cost?",
        a: "Sedan / Coupe $399, Small SUV / Crossover $449, Large SUV $499, Pickup Truck $549 — CAD before GST. Oversized or complex glass is quoted individually after we see the vehicle.",
      },
      {
        q: "Can I add it to a PPF package?",
        a: "Yes — it's available as a standalone service or added to any PPF package in the calculator at the same price, with no hidden surcharges.",
      },
    ],
  },

  "ceramic-coating-edmonton": {
    slug: "ceramic-coating-edmonton",
    serviceSlug: "ceramic-coating",
    title: "Ceramic Coating",
    h1: "Ceramic Coating in Edmonton",
    seoTitle: "Ceramic Coating Edmonton — WTD 3, 5 & 7-Year Packages",
    seoDescription:
      "WTD ceramic coating packages in Edmonton: 3-Year from $799, 5-Year from $899, 7-Year from $1,499. Wash, decontamination and light one-step correction included. Book online.",
    keywords:
      "ceramic coating Edmonton, car ceramic coating Edmonton, ceramic paint protection Edmonton, paint protection coating Edmonton",
    hero: GALLERY_IMAGES[4],
    tag: "Hydrophobic · Long-Lasting",
    related: ["ppf-edmonton", "paint-correction-edmonton", "exterior-detailing-edmonton"],
    intro:
      "WTD professional ceramic coating packages — 3-Year, 5-Year and 7-Year — with full paint preparation included in every price: thorough exterior hand wash, paint decontamination, clay treatment where necessary, surface preparation, light one-step paint correction, degreasing, professional application, proper curing time and a final quality inspection.",
    sections: [
      {
        heading: "Three packages, one standard of preparation",
        body:
          "Choose the protection period that fits how long you keep your vehicle: WTD 3-Year Ceramic (from $799), WTD 5-Year Ceramic (from $899) and WTD 7-Year Ceramic (from $1,499). Every package includes the same thorough preparation — wash, decontamination, clay treatment where necessary and a light one-step paint correction — because a coating is only as good as the surface underneath it. Severe paint defects or multi-stage correction are quoted separately, always before work starts.",
      },
      {
        heading: "What a ceramic coating actually does",
        body:
          "A professional ceramic coating is a chemically-bonded, hydrophobic layer over your clear coat: long-lasting water repellency, enhanced gloss and colour depth, easier washing and maintenance, reduced adhesion of environmental contaminants and improved resistance to environmental exposure. It is not a force field — it does not prevent stone chips, and no coating makes paint scratch-proof. For impact protection on high-risk panels, pair it with Paint Protection Film.",
      },
      {
        heading: "Curing & drop-off",
        body:
          "The coating must remain dry during its initial curing period according to manufacturer instructions — depending on the package and conditions, your vehicle may need to remain overnight at our studio for proper curing. We confirm the exact schedule when you book and walk you through the aftercare routine at pick-up.",
      },
      {
        heading: "Add Glass Protection",
        body:
          "Glass coating is not automatically included. Add Professional Glass Protection — a hydrophobic coating formulated specifically for automotive glass — for the windshield ($89) or all exterior glass (from $149) when you book.",
      },
    ],
    benefits: [
      "WTD 3-Year Ceramic — from $799",
      "WTD 5-Year Ceramic — from $899 (most popular)",
      "WTD 7-Year Ceramic — from $1,499",
      "Wash, decontamination, clay & light one-step correction included",
      "Long-lasting hydrophobic protection & easier washing",
      "Enhanced gloss and colour depth",
    ],
    faq: [
      {
        q: "Which package should I choose?",
        a: "Match the protection period to how long you plan to keep the vehicle: 3-Year for shorter ownership, 5-Year for most drivers, 7-Year for long-term keepers. Preparation is identical in every package — you're choosing the coating's rated protection period.",
      },
      {
        q: "Is paint correction included?",
        a: "A light one-step paint correction is included in every package. Severe paint defects or multi-stage correction are a separate service — we assess the paint together at drop-off and quote it before anything starts.",
      },
      {
        q: "Is ceramic coating scratch-proof?",
        a: "No. No coating is scratch-proof, and ceramic does not prevent stone chips. It adds a hard, slick, hydrophobic layer that makes washing easier and reduces adhesion of contaminants. For physical impact protection, add PPF on the impact zones.",
      },
      {
        q: "How long does the process take?",
        a: "A ceramic coating installation typically takes a full day, and your vehicle may need to remain overnight so the coating can cure properly — it must stay dry during its initial curing period per manufacturer instructions. We confirm the schedule when you book.",
      },
      {
        q: "Do I still need to wash my car?",
        a: "Yes — but far less often, and washing is dramatically easier. Dirt has nothing to bond to on a ceramic-coated surface, so contaminants rinse off with much less effort.",
      },
      {
        q: "Is glass coating included?",
        a: "No — glass coating is never automatically included. Professional Glass Protection (hydrophobic glass coating) is available as an add-on: windshield $89 or all exterior glass from $149.",
      },
      {
        q: "Is there a shorter-term ceramic option?",
        a: "Yes — 1-Year Ceramic Protection ($149) is available exclusively as an add-on with a Paint Correction service, when the paint has just been freshly prepared. It is not sold as a standalone package — our standalone coatings are the WTD 3-Year, 5-Year and 7-Year packages above.",
      },
    ],
  },

  "paint-correction-edmonton": {
    slug: "paint-correction-edmonton",
    serviceSlug: "paint-correction",
    title: "Paint Correction",
    h1: "Paint Correction in Edmonton",
    seoTitle: "Paint Correction Edmonton — Multi-Stage Polishing & Swirl Removal",
    seoDescription:
      "Machine paint correction in Edmonton. One-step and multi-stage polishing reduces swirl marks, holograms, oxidation and scratches. Deep-gloss finish restoration.",
    keywords:
      "paint correction Edmonton, swirl removal Edmonton, car polishing Edmonton, machine polish Edmonton",
    hero: GALLERY_IMAGES[3],
    tag: "Mirror Finish",
    related: ["ceramic-coating-edmonton", "ppf-edmonton", "exterior-detailing-edmonton"],
    intro:
      "Machine polishing — from one-step gloss enhancement to multi-stage correction — reduces swirl marks, holograms, oxidation and micro-scratches, restoring deep reflection.",
    sections: [
      {
        heading: "What paint correction really is",
        body:
          "Paint correction is the mechanical process of leveling the clear coat to remove defects — swirls from automatic car washes, holograms from bad detailing, hard water spots, bird-drop etching, oxidation and light scratches. It is not a wax or a polish-and-shine trick. It's the actual removal of a microscopic layer of damaged clear coat to expose clean, level paint underneath.",
      },
      {
        heading: "One-step or multi-stage",
        body:
          "Not every vehicle needs the same correction. Our one-step correction improves gloss and reduces light swirls — perfect for newer or well-kept paint. Multi-stage correction is for vehicles with heavier defects: a cutting step, refinement and finishing polish. We inspect your paint under focused LED lighting and recommend the right option for your vehicle and goals — the final result depends on paint condition and safe clear-coat limits.",
      },
      {
        heading: "The right tools & the right hands",
        body:
          "We use professional dual-action polishers and rotary machines with premium foam, wool and microfiber pads. Compounds and polishes are professional-grade. Paint depth is assessed where appropriate before correction to help determine a safe polishing approach.",
      },
      {
        heading: "Protect the result",
        body:
          "After correcting the paint, protect that freshly polished finish with 1-Year Ceramic Protection for just $149 when added to a Paint Correction service. It helps preserve the freshly corrected gloss, adds strong hydrophobic water behavior, makes routine washing easier and adds environmental protection — up to 1 year of protection with proper maintenance. Choose One-Step or Multi-Stage Correction in the calculator below and add it there in one tap.",
      },
    ],
    benefits: [
      "Reduces swirls, holograms, oxidation, marring",
      "Restores deep, glossy reflection",
      "Paint depth assessment where appropriate",
      "Ideal prep step before ceramic coating or PPF",
      "One-Step & Multi-Stage packages available",
      "Focused-light inspection at every stage",
    ],
    faq: [
      {
        q: "Will paint correction remove deep scratches?",
        a: "It depends on depth. If the scratch has not gone through the clear coat, we can usually remove it. Scratches that penetrate to the base coat or primer require touch-up paint before correction.",
      },
      {
        q: "How long does paint correction take?",
        a: "One-step correction usually takes about a day. Multi-stage correction on a large vehicle can take 2 to 4 days, depending on paint condition.",
      },
      {
        q: "Do I need paint correction if I'm getting ceramic coating?",
        a: "Not always. Ceramic coating locks in the appearance of the paint underneath it. If the paint already looks good, correction may not be necessary. If you want swirls, oxidation or other defects improved before coating, we'll recommend the appropriate One-Step or Multi-Stage Correction separately.",
      },
      {
        q: "Will you damage my paint?",
        a: "No. We assess clear-coat thickness where appropriate and only remove the minimum necessary to level defects safely. The final result depends on paint condition and safe clear-coat limits.",
      },
      {
        q: "What defects can paint correction remove?",
        a: "Swirl marks from automatic washes, buffer holograms, light scratches that haven't broken through the clear coat, water-spot etching, bird-drop etching and oxidation. Deep stone chips and scratches that reach the base coat or primer can't be polished out — those need touch-up work first, and we'll point them out before we start.",
      },
      {
        q: "What's the difference between polishing and paint correction?",
        a: "A quick polish (or 'polish-and-wax') adds temporary gloss and can hide fine swirls for a few weeks. Paint correction permanently removes a microscopic layer of clear coat to physically level the defects — the swirls are gone, not hidden. Correction is measured in stages and verified under focused lighting.",
      },
    ],
  },

  "interior-detailing-edmonton": {
    slug: "interior-detailing-edmonton",
    serviceSlug: "interior-detailing",
    title: "Interior Detailing",
    h1: "Interior Detailing in Edmonton",
    seoTitle: "Interior Detailing Edmonton — Regular Detail & Deep Interior Cleaning",
    seoDescription:
      "Two levels of interior care in Edmonton: Regular Interior Detail (~1.5–2 h, from $149) and Deep Interior Cleaning with extraction & steam (~3–4 h, from $299). Book online and drop your vehicle off.",
    keywords:
      "interior detailing Edmonton, car interior cleaning Edmonton, deep interior cleaning Edmonton, upholstery extraction Edmonton, steam cleaning car interior",
    hero: GALLERY_IMAGES[1],
    tag: "Two Levels",
    related: ["odor-removal-edmonton", "exterior-detailing-edmonton", "ceramic-coating-edmonton"],
    intro:
      "Two clear levels of interior care: a fast Regular refresh (~1.5–2 hours) and a deeper Deep Interior Cleaning with extraction and steam (~3–4 hours). Drop your vehicle off — we'll notify you when it's ready.",
    sections: [
      {
        heading: "Regular Interior Detail — ~1.5–2 hours, from $149",
        body:
          "Maintenance interior refresh for vehicles with normal everyday dirt: complete interior vacuuming, seats, carpets and floor mats; dashboard, centre console, cup holders, door panels and accessible trim; interior windows and mirrors; light trunk/cargo area clean; and a final interior inspection. Carpet/seat shampoo extraction, steam cleaning, headliner deep cleaning, heavy stain removal and extensive pet hair removal are not included — choose Deep Interior Cleaning for that.",
      },
      {
        heading: "Deep Interior Cleaning — ~3–4 hours, from $299",
        body:
          "Complete interior deep cleaning with shampoo extraction and steam treatment included. Allow approximately 3–4 hours with a minimum of two technicians depending on vehicle condition: shampoo extraction of fabric seats and carpets, deep cleaning of floor mats, headliner cleaning using material-safe methods, steam cleaning of suitable surfaces, vents, buttons, crevices and door pockets, interior glass, trunk and removable trunk mats. Leather seats receive material-appropriate cleaning and conditioning. Extreme contamination, biohazards and extensive pet hair may require additional charges — quoted up front, never without your approval.",
      },
      {
        heading: "What's included — Regular",
        body:
          "Complete interior vacuuming · Vacuum seats and carpets · Vacuum floor mats · Clean dashboard and centre console · Clean cup holders · Clean door panels and accessible interior plastic surfaces · Wipe accessible interior trim · Clean interior windows and mirrors · Light cleaning of trunk/cargo area · Final interior inspection.",
      },
      {
        heading: "What's included — Deep",
        body:
          "Complete interior vacuuming · Shampoo extraction of fabric seats · Shampoo extraction of carpets · Deep cleaning of floor mats · Headliner cleaning using appropriate safe methods · Steam cleaning of suitable interior surfaces · Thorough cleaning of dashboard and centre console · Detailed cleaning of cup holders · Cleaning of vents, buttons, crevices and accessible tight areas · Deep cleaning of door panels and door pockets · Cleaning and conditioning of appropriate interior plastic surfaces · Cleaning of trunk/cargo area · Cleaning and washing of removable trunk mats where suitable · Interior glass and mirror cleaning · Final interior inspection.",
      },
      {
        heading: "Which one do you need?",
        body:
          "Regular is perfect for maintained vehicles and routine refreshes. Choose Deep when the fabric feels worn-in, carpets hold embedded dirt, seats need extraction, or the vehicle hasn't had a proper interior clean in a long time. Not sure? Book Regular — at drop-off we'll honestly tell you if Deep makes more sense for your vehicle's condition, and the difference is only the upgrade, never a re-booking.",
      },
    ],
    benefits: [
      "Regular Interior Detail — from $149 (about 1.5–2 hours)",
      "Deep Interior Cleaning — from $299 (about 3–4 hours, minimum two technicians)",
      "Shampoo extraction & steam included in Deep — never an extra charge",
      "Headliner cleaning with material-safe methods",
      "Pet hair removal & heavy soil surcharge — quoted up front, never without your approval",
      "Drop off your vehicle — we'll notify you when it's ready",
    ],
    faq: [
      {
        q: "What's the difference between Regular and Deep interior cleaning?",
        a: "Regular (~1.5–2 h) is a thorough refresh: vacuum, panels, glass, mats, light trunk clean. Deep (~3–4 h, minimum two technicians) adds shampoo extraction of fabric seats and carpets, deep floor-mat cleaning, headliner cleaning using material-safe methods, steam cleaning of suitable surfaces, and detailed crevice/vent/door-pocket treatment.",
      },
      {
        q: "Is shampoo extraction included in Deep Interior Cleaning?",
        a: "Yes — shampoo extraction of fabric seats and carpets is included as standard, not an extra charge. Steam cleaning is also included wherever safe and appropriate.",
      },
      {
        q: "Can you remove pet hair and stains?",
        a: "Pet hair removal is an extra service when excessive (from $50). Localized heavy stain cleaning starts at $35; severe or biological contamination is assessed separately and quoted before any work starts. We don't promise complete removal of permanent stains.",
      },
      {
        q: "Do you clean the headliner?",
        a: "Yes — in Deep Interior Cleaning we clean the headliner using methods appropriate for the material (low-moisture techniques to avoid water-staining the backing). Heavily soiled headliners may need a condition-based surcharge, confirmed with you at drop-off before any work starts.",
      },
      {
        q: "What about leather seats?",
        a: "Leather seats receive material-appropriate cleaning and conditioning rather than fabric shampoo extraction. Cracked leather requires re-dye or re-upholstery — services we can refer you to.",
      },
      {
        q: "Is ozone treatment included?",
        a: "No — Ozone Odor Treatment is an optional add-on ($69). It's available for Regular Interior Detail and can be added at booking.",
      },
    ],
  },

  // Owner (2026-11): decontamination is an ADD-ON to Exterior Detail, not a
  // standalone service. The old /decontamination-wash-edmonton URL redirects here.
  "exterior-detailing-edmonton": {
    slug: "exterior-detailing-edmonton",
    serviceSlug: "exterior-detailing",
    title: "Exterior Detailing",
    h1: "Exterior Detailing in Edmonton",
    seoTitle: "Exterior Detailing Edmonton — Hand Wash, Wheels & Paint Decontamination Add-On",
    seoDescription:
      "Professional exterior detailing in Edmonton: pre-wash, hand wash, wheels, tires and wheel wells, bug and grime removal, exterior glass and tire dressing. Add clay + iron paint decontamination when your paint needs it.",
    keywords:
      "exterior detailing Edmonton, exterior car detail Edmonton, hand wash Edmonton, paint decontamination Edmonton, clay bar Edmonton, iron removal Edmonton",
    hero: GALLERY_IMAGES[3],
    tag: "Hand Wash + Decon Add-On",
    related: ["interior-detailing-edmonton", "paint-correction-edmonton", "ceramic-coating-edmonton"],
    intro:
      "A careful, professional exterior detail — plus an optional paint decontamination add-on that strips bonded iron, tar and road film when your paint needs a true reset.",
    sections: [
      {
        heading: "What's included",
        body:
          "Thorough exterior hand wash using automotive shampoo · Cleaning of body panels · Cleaning of exterior windows and mirrors · Cleaning of wheels and rims · Iron remover treatment on wheels · Cleaning of accessible wheel areas · Tire cleaning · Tire shine application · Final exterior inspection.",
      },
      {
        heading: "Not included",
        body:
          "Full-body chemical decontamination, clay bar treatment, machine polishing, paint correction, ceramic coating and engine bay cleaning are not part of the standard Exterior Detail. Full-body iron removal or clay treatment is never added automatically — add the Paint Decontamination add-on when you book.",
      },
      {
        heading: "Optional upgrade — Paint Decontamination",
        body:
          "Upgrade your exterior detail with professional paint decontamination to remove embedded iron particles, road tar and bonded contaminants that regular washing cannot eliminate. Iron fallout removal, tar removal where required, clay bar treatment where appropriate, final rinse and protection. Add it to an Exterior Detail or a Full Interior + Exterior Detail; it's priced for your vehicle size — From $71.",
      },
      {
        heading: "The honest part",
        body:
          "Decontamination dramatically improves paint smoothness, but it is not paint correction — it won't remove scratches or swirls, and heavy contamination can require additional time. If your vehicle needs more, we'll tell you before we start, not after.",
      },
    ],
    benefits: [
      "Pre-wash + proper hand wash",
      "Wheels, tires & accessible wheel wells",
      "Bug & grime removal",
      "Exterior glass + tire dressing",
      "Optional add-on: iron / fallout removal",
      "Optional add-on: clay-bar decontamination where required",
    ],
    faq: [
      {
        q: "Is decontamination included in the Exterior Detail?",
        a: "No — it's an optional add-on. Choose Exterior Detail (or Full Interior + Exterior Detail) and add Paint Decontamination in the calculator or at booking.",
      },
      {
        q: "How is decontamination different from a regular wash?",
        a: "A regular wash removes loose dirt. Decontamination removes what chemically bonds to paint — iron particles, tar, road film — using dedicated chemistry and clay treatment.",
      },
      {
        q: "Will it remove scratches?",
        a: "No — that's paint correction. Decon prepares paint beautifully and is the ideal first step before correction or ceramic coating.",
      },
      {
        q: "How often should I decontaminate?",
        a: "For Edmonton driving, once or twice a year — typically spring (after winter salt) and fall (before winter protection).",
      },
    ],
  },

  "bed-liner-edmonton": {
    slug: "bed-liner-edmonton",
    serviceSlug: "bed-liner",
    title: "Bed Liner",
    h1: "Spray-On Bed Liner in Edmonton",
    seoTitle: "Bed Liner Edmonton — Spray-On Truck Bed Coating",
    seoDescription:
      "Professional spray-on bed liner installation in Edmonton. Durable textured truck bed coating with proper preparation, masking and a controlled installation environment.",
    keywords:
      "bed liner Edmonton, spray bed liner Edmonton, spray-on bedliner Edmonton, truck bed coating Edmonton",
    hero: GALLERY_IMAGES[2],
    tag: "Spray-On",
    related: ["ppf-edmonton", "ceramic-coating-edmonton", "paint-correction-edmonton"],
    intro:
      "Professional spray-on bed liner installation — a tough, textured protective coating that turns your truck bed into a dependable workhorse.",
    sections: [
      {
        heading: "Why spray-on is different",
        body:
          "A professionally applied spray-on coating creates a textured, non-slip surface that stands up to daily abuse that would damage a factory bed. Unlike drop-in plastic liners that trap moisture and can rot the truck bed, spray-on coating bonds directly to the metal — long-term protection with no gap for corrosion to start.",
      },
      {
        heading: "Not just truck beds",
        body:
          "We spray truck beds, rocker panels, tailgates, bumpers, off-road accessories, ATV trays, jeep interiors, and full commercial applications.",
      },
      {
        heading: "The install matters more than the product",
        body:
          "A spray-on coating is only as good as the surface prep. At WTD, every install includes proper masking, sanding, degreasing, and multi-coat spray application in a controlled environment. Cheap installs skip the prep and peel within a year. Ours don't.",
      },
    ],
    benefits: [
      "Professional spray-on protective coating",
      "Textured non-slip finish",
      "Durable finish built for heavy-duty use",
      "Bonds directly to metal — no moisture trap",
      "Full masking & professional prep on every install",
      "Short bed & long bed options",
    ],
    faq: [
      {
        q: "How long does a bed liner last?",
        a: "A properly prepped and professionally installed spray-on liner is a long-term protective solution for the bed. Exact lifespan depends on use and the product applied — we'll confirm the details of the product being used before installation.",
      },
      {
        q: "Can you match the color of my truck?",
        a: "Colour options depend on the product line in use — ask us what's currently available when you book.",
      },
      {
        q: "How long does the install take?",
        a: "A full truck bed install with proper prep and cure time typically takes 1-2 days.",
      },
      {
        q: "Can you spray other parts?",
        a: "Yes. Rocker panels, tailgates, bumpers, ATV trays, off-road accessories, and full commercial applications are all options.",
      },
    ],
  },

  "headlight-restoration-edmonton": {
    slug: "headlight-restoration-edmonton",
    serviceSlug: "headlight-restoration",
    title: "Headlight Restoration",
    h1: "Headlight Restoration in Edmonton",
    seoTitle: "Headlight Restoration Edmonton — Clarity, Safety & Curb Appeal",
    seoDescription:
      "Professional headlight restoration in Edmonton. Multi-stage sanding, polishing and UV-sealant. Restore cloudy, yellowed lenses to optical clarity.",
    keywords:
      "headlight restoration Edmonton, cloudy headlights Edmonton, yellowed headlights Edmonton, headlight polish Edmonton",
    hero: GALLERY_IMAGES[6],
    tag: "Optical Clarity",
    related: ["paint-correction-edmonton", "ceramic-coating-edmonton", "interior-detailing-edmonton"],
    intro:
      "Cloudy, yellowed, oxidized headlight lenses restored to optical clarity — dramatically safer night driving and a sharper vehicle front-end.",
    beforeAfterNote:
      "Recent restoration in our Edmonton studio — multi-stage wet-sanding, machine polish and a UV-blocking clear-coat sealer.",
    sections: [
      {
        heading: "Why headlights go cloudy",
        body:
          "Modern headlight lenses are made of polycarbonate — a plastic that oxidizes under years of UV exposure. In Alberta, with our long summer daylight hours, headlights can be visibly yellowed within 5-7 years. The result is not just cosmetic: cloudy lenses can cut usable light output by 50% or more, dramatically reducing your night-time visibility and safety.",
      },
      {
        heading: "Real restoration, not a wipe-and-hope",
        body:
          "Our restoration is a multi-stage process: wet-sanding through progressively finer grits to remove the oxidized layer, polishing with high-quality compound to restore optical clarity, then sealing with a professional UV-blocking clear coat. How long the result lasts depends on lens condition, UV exposure, maintenance and the protection system applied. Cheap toothpaste tricks and off-the-shelf kits skip the UV sealer step, which is why the yellowing returns within months.",
      },
      {
        heading: "Also great for resale",
        body:
          "Nothing dates a vehicle faster than cloudy headlights. Restoring them is one of the highest-return-on-investment visual upgrades before selling or trading in a used vehicle.",
      },
    ],
    benefits: [
      "Multi-stage wet-sanding process",
      "Professional UV-blocking clear coat sealer",
      "Significantly improves clarity on suitable lenses",
      "Improved night-time visibility & safety",
      "Fast turnaround (usually same day)",
      "Perfect resale-prep upgrade",
    ],
    faq: [
      {
        q: "How long does headlight restoration last?",
        a: "Durability depends on lens condition, UV exposure, maintenance and the protection applied. Kits without a proper UV-sealer step typically re-yellow much faster — that's why we finish with a UV-blocking clear coat.",
      },
      {
        q: "How long does it take?",
        a: "Most headlight restorations are completed same-day, typically in 1 to 2 hours.",
      },
      {
        q: "Can severely yellowed lenses be saved?",
        a: "In most cases, yes. If oxidation has cracked the lens surface or damaged the internal reflector, replacement may be necessary.",
      },
      {
        q: "What about damage inside the headlight?",
        a: "Internal damage — cracking, moisture inside the lens, failed seals or reflector damage — cannot be corrected by exterior restoration. We'll inspect first and tell you honestly if restoration won't solve it; those defects are not covered by this service.",
      },
      {
        q: "Is restoration better than replacing the headlight?",
        a: "When the damage is surface oxidation — cloudiness and yellowing — restoration is usually the sensible choice: it costs a fraction of a new OEM headlight assembly, which can run hundreds of dollars per side. If the lens is cracked or the reflector is damaged inside, replacement is the right call, and we'll tell you that upfront.",
      },
    ],
  },

  "odor-removal-edmonton": {
    slug: "odor-removal-edmonton",
    serviceSlug: "odor-removal",
    title: "Odor Treatment",
    h1: "Odor Treatment & Removal in Edmonton",
    seoTitle: "Odor Removal & Ozone Treatment Edmonton — Smoke, Pet & Mildew",
    seoDescription:
      "Source-first odor removal in Edmonton: inspection, targeted cleaning, enzyme and ozone treatment. Smoke, pet, spill and mildew odors. Severe cases inspected and quoted honestly.",
    keywords:
      "odor removal Edmonton, smoke smell removal car Edmonton, pet odor removal car Edmonton, ozone treatment Edmonton",
    hero: GALLERY_IMAGES[7],
    tag: "Source-First Treatment",
    related: ["interior-detailing-edmonton", "exterior-detailing-edmonton", "ceramic-coating-edmonton"],
    intro:
      "Source-first odor removal for vehicles — smoke, pet, spill, mildew. We find and clean the source, then apply the right treatment: enzyme where appropriate, ozone where appropriate. Honest pricing, no masking sprays.",
    sections: [
      {
        heading: "Why regular cleaning doesn't work",
        body:
          "Odor molecules bond deep into fabrics, carpets, headliners, foam padding and even the HVAC system. Regular shampooing only cleans the surface. Air fresheners just mask the problem for hours. We inspect for the actual source, clean it with targeted chemistry, then neutralize what remains with enzyme or ozone treatment — chosen for your situation, not by default.",
      },
      {
        heading: "What we treat",
        body:
          "Smoke (cigarette, cigar, cannabis), pet odors and urine, spilled milk, vomit, mildew from water damage, food odors, and lingering perfume. Severe cases — heavy smoke, biological contamination, mold-related issues — require an inspection so we can tell you honestly what it will take and quote a final price.",
      },
      {
        heading: "Ozone treatment, on its own",
        body:
          "Already had the source cleaned and just need the final neutralization? Ozone Treatment ($69) is available as an optional add-on to our Odor Treatment. It's a treatment only — not a replacement for interior cleaning — and we won't promise that ozone alone permanently removes every odor.",
      },
    ],
    benefits: [
      "Source inspection & targeted cleaning",
      "Enzyme treatment where appropriate",
      "Ozone treatment where appropriate",
      "Reaches HVAC system & hidden crevices",
      "Safe for all interior materials",
      "Honest assessment — severe cases quoted after inspection",
    ],
    faq: [
      {
        q: "What's the difference between Odor Removal and Ozone Treatment?",
        a: "Ozone Treatment ($69) is an optional add-on — it helps neutralize lingering odor after the source has been cleaned or removed, and it's not a replacement for interior cleaning. Odor Treatment (from $99) is the full job: we inspect for the source, clean it, and apply the appropriate enzyme and/or ozone treatment. Severe situations — smoke, pet urine, vomit, milk spills, biological or mold issues — require an inspection before we can quote a final price.",
      },
      {
        q: "Is odor removal permanent?",
        a: "Treatment neutralizes the odor molecules present at the time of service, once the source has been removed. If the source (a smoker, a pet, a leak) continues, new odor will re-accumulate over time.",
      },
      {
        q: "Do I need an interior detail too?",
        a: "For heavily contaminated vehicles, we strongly recommend pairing odor treatment with a full interior detail. Cleaning the surface plus neutralizing the deep odor gives the best result.",
      },
      {
        q: "How long does the treatment take?",
        a: "Treatment itself takes about 30-45 minutes plus dwell time; source cleaning adds time depending on condition. You'll typically have the vehicle back the same day.",
      },
      {
        q: "Is it safe for leather and electronics?",
        a: "Yes. Our treatments are safe for all interior materials, including leather, alcantara, electronics and HVAC components.",
      },
      {
        q: "What types of odors can you treat?",
        a: "Cigarette and cannabis smoke, pet odors and urine, spilled milk and food, vomit, mildew from water or moisture, and lingering perfume. Severe or biological cases start with an inspection so we can tell you honestly what level of treatment is realistic.",
      },
      {
        q: "Does odor treatment replace interior cleaning?",
        a: "No — and any shop that says otherwise is masking the problem. Treatment neutralizes odor; it doesn't remove the source. That's why our Odor Removal service starts with finding and cleaning the source, and why we recommend pairing treatment with a full interior detail on heavily contaminated vehicles.",
      },
    ],
  },
};

export function getServicePage(slug) {
  return SERVICE_PAGES[slug] || null;
}

// Short service slug → pricing-engine service keys (single source of truth
// for every price shown on the public site)
export const PRICING_KEYS = {
  "window-tint": [],
  "ppf": [],
  "ceramic-coating": ["ceramic-3yr", "ceramic-5yr", "ceramic-7yr"],
  "paint-correction": ["paint-correction-1step", "paint-correction-multi"],
  "interior-detailing": ["interior-detail", "full-detail"],
  "exterior-detailing": ["exterior-detail", "full-detail"],
  "headlight-restoration": ["headlight-restoration"],
  "odor-removal": ["odor-removal"],
  "bed-liner": ["bed-liner"],
};

// Services priced outside the pricing-config engine (PPF packages and
// windshield film are quoted from their own package/vehicle tables on their
// service pages). Their advertised starting prices, shown when the engine
// has no matching key — keep in sync with those pages.
export const STATIC_FROM_PRICE = {
  "ppf": 699,
  "windshield-protection": 399,
};

// Primary pricing key per service-page slug — the service pre-selected in the
// embedded booking flow when a visitor taps a CTA on that service page.
export const PRIMARY_PRICING_KEY = {
  "ceramic-coating": "ceramic-5yr",
  "paint-correction": "paint-correction-1step",
  "interior-detailing": "interior-detail",
  "exterior-detailing": "exterior-detail",
  "headlight-restoration": "headlight-restoration",
  "odor-removal": "odor-removal",
  "bed-liner": "bed-liner",
};

// Lowest advertised price for a short slug from a pricing-config services list
export function minPriceFor(slug, services) {
  const keys = PRICING_KEYS[slug] || [];
  const vals = (services || [])
    .filter((s) => keys.includes(s.key))
    .map((s) => {
      if (s.options?.length) return Math.min(...s.options.map((o) => o.price));
      if ((s.price_kind || "").startsWith("flat")) return s.flat_price;
      const p = Object.values(s.prices || {}).filter((v) => v != null);
      return p.length ? Math.min(...p) : null;
    })
    .filter((v) => v != null);
  if (vals.length) return Math.min(...vals);
  return STATIC_FROM_PRICE[slug] != null ? STATIC_FROM_PRICE[slug] : null;
}

// { from, promoFrom } for a short slug — promoFrom is set only when the
// cheapest matching service is promo-eligible (single source: pricing config).
export function priceInfoFor(slug, services, promo) {
  const keys = PRICING_KEYS[slug] || [];
  const mins = (services || [])
    .filter((s) => keys.includes(s.key))
    .map((s) => {
      let min = null;
      if (s.options?.length) min = Math.min(...s.options.map((o) => o.price));
      else if ((s.price_kind || "").startsWith("flat")) min = s.flat_price;
      else {
        const p = Object.values(s.prices || {}).filter((v) => v != null);
        min = p.length ? Math.min(...p) : null;
      }
      return { min, key: s.key };
    })
    .filter((v) => v.min != null && v.min > 0);
  if (!mins.length) {
    const s = STATIC_FROM_PRICE[slug];
    return { from: s != null ? s : null, promoFrom: null };
  }
  const best = mins.reduce((a, b) => (b.min < a.min ? b : a));
  const eligible =
    promo && promo.active && (promo.services || []).includes(best.key) &&
    !(promo.expires_at && new Date(`${promo.expires_at}T23:59:59`) < new Date());
  return {
    from: best.min,
    promoFrom: eligible ? Math.floor(best.min * (100 - (Number(promo.service_percents?.[best.key]) || promo.percent || 50)) / 100) : null,
  };
}

export function allServiceSlugs() {
  return Object.keys(SERVICE_PAGES);
}
